import { Action } from '@/components/permissions/Action';
import { PermissionNameFor } from '@/components/permissions/Permission';
import { Resource } from '@/components/permissions/Resource';
import { serviceBusinessPaths } from '@/feature/service-business/data/serviceBusinessConfig';
import { isTenantRouteScope } from '@/routes/routeScope';

const loginPath = '/login';
const returnToParam = 'returnTo';

const appAdminPermissions = [
  PermissionNameFor(Resource.Admin, Action.View),
  PermissionNameFor(Resource.Tenants, Action.View)
];

const customerPortalPermissions = [
  'Permissions.CustomerPortal.View',
  'Permissions.CustomerAccounts.View',
  'Permissions.CustomerAccount.View'
];

const tenantBackofficePermissions = [
  PermissionNameFor(Resource.Dashboard, Action.View),
  PermissionNameFor(Resource.Order, Action.View),
  PermissionNameFor(Resource.Customers, Action.View),
  PermissionNameFor(Resource.Employees, Action.View),
  PermissionNameFor(Resource.Messenger, Action.View),
  PermissionNameFor(Resource.Settings, Action.View),
  PermissionNameFor(Resource.Website, Action.View)
];

function hasAnyPermission(
  permissions: string[] | null | undefined,
  requiredPermissions: string[]
) {
  return requiredPermissions.some((permission) =>
    permissions?.includes(permission)
  );
}

export function hasAppAdminAccess(permissions: string[] | null | undefined) {
  return hasAnyPermission(permissions, appAdminPermissions);
}

export function hasCustomerPortalAccess(
  permissions: string[] | null | undefined
) {
  return hasAnyPermission(permissions, customerPortalPermissions);
}

function hasTenantBackofficeAccess(permissions: string[] | null | undefined) {
  return hasAnyPermission(permissions, tenantBackofficePermissions);
}

export function canUseCurrentDomainSession(
  permissions: string[] | null | undefined
) {
  return !isTenantRouteScope() || !hasAppAdminAccess(permissions);
}

export function resolvePostLoginPath(permissions: string[] | null | undefined) {
  if (!isTenantRouteScope() && hasAppAdminAccess(permissions)) {
    return '/admin';
  }

  if (
    hasCustomerPortalAccess(permissions) &&
    !hasTenantBackofficeAccess(permissions) &&
    !hasAppAdminAccess(permissions)
  ) {
    return '/client';
  }

  return serviceBusinessPaths.backofficeBasePath;
}

export function normalizeReturnTo(value: string | null | undefined) {
  const trimmed = value?.trim();

  if (!trimmed || !trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return undefined;
  }

  if (trimmed === loginPath || trimmed.startsWith(`${loginPath}?`)) {
    return undefined;
  }

  return trimmed;
}

export function getReturnToFromSearch(search: string) {
  return normalizeReturnTo(new URLSearchParams(search).get(returnToParam));
}

export function getCurrentReturnToPath() {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function createLoginPathWithReturnTo(
  returnTo: string | null | undefined
) {
  const normalizedReturnTo = normalizeReturnTo(returnTo);

  if (!normalizedReturnTo) {
    return loginPath;
  }

  const params = new URLSearchParams({
    [returnToParam]: normalizedReturnTo
  });

  return `${loginPath}?${params.toString()}`;
}

export function redirectToLoginWithReturnTo(
  returnTo: string | null | undefined = getCurrentReturnToPath()
) {
  if (
    typeof window === 'undefined' ||
    window.location.pathname.startsWith(loginPath)
  ) {
    return;
  }

  sessionStorage.removeItem('accessToken');
  sessionStorage.removeItem('refreshToken');
  window.location.assign(createLoginPathWithReturnTo(returnTo));
}

export function resolvePostLoginRedirect(
  permissions: string[] | null | undefined,
  returnTo: string | null | undefined
) {
  return normalizeReturnTo(returnTo) ?? resolvePostLoginPath(permissions);
}
